// src/pages/Enhancer.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import GenerateEnhancerModal from "../components/GenerateEnhancerModal";
import placeholderImage from "../assets/placeholder.jpg";
import {
  Plus,
  Download,
  Trash2,
  X,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import Loader from "../components/Loader";

const FEATURE = "enhancer";
const PAGE_SIZE = 12;

export default function Enhancer() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  // ✅ Prefer URL param as source of truth (matches BG-Replacer pattern)
  const projectId = paramProjectId || selectedProduct?.id;

  // items: [{ id, url, status, created_at }]
  const [items, setItems] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { url, id }
  const [error, setError] = useState(null);

  // Quota state (+ gating flags)
  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,
    period_start: null,
    period_end: null,

    // optional server fields (won’t break if absent)
    payment_blocked: false,
    sub_status: null,
    is_blocked: false,
    interval_months: 1,
  });

  // Page-level project loading
  const [projectLoading, setProjectLoading] = useState(true);

  // ✅ Infinite scroll (ObjectRemover pattern)
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  const previewBackdrop = useRef(null);
  const pollTimerRef = useRef(null);

  /* ----------------------------- Helpers ----------------------------- */
  const getMeta = useCallback((m) => {
    if (!m) return {};
    if (typeof m === "string") {
      try {
        return JSON.parse(m);
      } catch {
        return {};
      }
    }
    return m;
  }, []);

  // prefer assetsByFeature when available; fallback to flat assets
  const pickEnhancerAssets = useCallback(
    (data) => data?.assetsByFeature?.[FEATURE] || data?.assets || [],
    []
  );

  // ✅ stable hasMore recalculation (do NOT depend on `page`)
  const recalcHasMore = useCallback((arr, pageNum) => {
    const p = Number(pageNum) || 1;
    setHasMore(p * PAGE_SIZE < (arr?.length || 0));
  }, []);

  // map server rows -> UI items
  const mapAssets = useCallback(
    (rows) =>
      (rows || [])
        .map((a) => {
          const meta = getMeta(a.metadata);
          const feature = String(meta?.feature || "").toLowerCase();

          const statusRaw = String(meta?.status || "").toLowerCase();
          const isDeleted = meta?.deleted === true || statusRaw === "deleted";
          const isFailed = statusRaw === "failed";

          // ✅ Hide soft-deleted + failed rows (must remain in DB for quota)
          if (isDeleted || isFailed) return null;

          const url = a.url || "";
          const looksPending =
            !url || (typeof url === "string" && url.includes("#"));

          const status = String(
            (meta.status || (looksPending ? "pending" : "finished")) || ""
          ).toLowerCase();

          return {
            id: a.id,
            url: looksPending ? "" : url,
            status,
            feature,
            created_at: a.created_at || a.createdAt || 0,
          };
        })
        .filter(Boolean)
        .filter((a) => a.feature === FEATURE)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [getMeta]
  );

  const anyPendingLocal = useMemo(() => {
    const PENDING = new Set(["pending", "queued", "processing"]);
    return items.some((i) => PENDING.has(i.status) || !i.url);
  }, [items]);

  const visibleItems = useMemo(
    () => items.slice(0, page * PAGE_SIZE),
    [items, page]
  );

  // Keep pageRef in sync (so merges can compute hasMore without causing refetch loops)
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Also keep hasMore in sync whenever page/items change
  useEffect(() => {
    setHasMore(page * PAGE_SIZE < items.length);
  }, [page, items.length]);

  /* ----------------------------- Quota ----------------------------- */
  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/enhancer/quota`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuota({ loading: false, ...res.data });
    } catch (e) {
      setQuota((q) => ({ ...q, loading: false }));
      if (e?.response?.status === 401) {
        setError("Session expired. Please sign in again.");
      }
    }
  }, [getToken]);

  /* --------------------------- Fetch project --------------------------- */
  const fetchProject = useCallback(async () => {
    // ✅ If no analyzed project yet, don’t error — just show CTA and empty grid.
    if (!projectId) {
      setProductImages([]);
      setItems((prev) => prev.filter((p) => String(p.id).startsWith("temp-"))); // keep optimistic only
      setPage(1);
      pageRef.current = 1;
      setHasMore(false);
      setError(null);
      setProjectLoading(false);
      return;
    }

    setProjectLoading(true);
    setPage(1);
    pageRef.current = 1;

    try {
      const token = await getToken();
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // product images for modal
      const imgs = Array.isArray(data.images)
        ? data.images
        : typeof data.images === "string"
        ? (() => {
            try {
              const parsed = JSON.parse(data.images);
              return Array.isArray(parsed) ? parsed : [data.images];
            } catch {
              return [data.images];
            }
          })()
        : [];
      setProductImages(imgs);

      // enhancer assets
      const serverList = mapAssets(pickEnhancerAssets(data));

      setItems((prev) => {
        const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
        const serverById = new Map(serverList.map((a) => [String(a.id), a]));
        const keptTemps = temps.filter((t) => !serverById.has(String(t.id)));
        const merged = [...keptTemps, ...serverList];

        recalcHasMore(merged, 1);
        return merged;
      });

      setError(null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Fetch project failed:", e?.response?.data || e.message);
      setError(
        e?.response?.status === 401
          ? "Session expired. Please sign in again."
          : "Failed to fetch project data"
      );
    } finally {
      setProjectLoading(false);
    }
  }, [getToken, projectId, mapAssets, pickEnhancerAssets, recalcHasMore]);

  // Initial load: project + quota
  useEffect(() => {
    void fetchProject();
    void fetchQuota();
  }, [fetchProject, fetchQuota]);

  // ✅ Only sync URL from dropdown when URL has no projectId (matches BG-Replacer)
  useEffect(() => {
    if (!paramProjectId && selectedProduct?.id) {
      navigate(`/generate/enhancer/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  /* ---------------------------- Polling ---------------------------- */
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const beginPolling = useCallback(async () => {
    if (!projectId) return;

    stopPolling();
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const token = await getToken();
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const serverList = mapAssets(pickEnhancerAssets(res.data));

        setItems((prev) => {
          const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
          const serverById = new Map(serverList.map((a) => [String(a.id), a]));
          const keptTemps = temps.filter((t) => !serverById.has(String(t.id)));
          const merged = [...keptTemps, ...serverList];
          recalcHasMore(merged, pageRef.current);
          return merged;
        });

        const PENDING = new Set(["pending", "queued", "processing"]);
        const stillPending = serverList.some(
          (a) => PENDING.has((a.status || "").toLowerCase()) || !a.url
        );

        if (!stillPending || attempts >= 40) {
          stopPolling();
          void fetchQuota();
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          setError("Session expired. Please sign in again.");
          stopPolling();
          return;
        }
        if (attempts >= 40) {
          stopPolling();
          void fetchQuota();
        }
      }
    };

    await tick();
    pollTimerRef.current = setInterval(tick, 5000);
  }, [
    getToken,
    projectId,
    mapAssets,
    pickEnhancerAssets,
    stopPolling,
    fetchQuota,
    recalcHasMore,
  ]);

  useEffect(() => {
    if (anyPendingLocal) void beginPolling();
  }, [anyPendingLocal, beginPolling]);

  /* ------------------------------ CTA gating (same as Remove BG) ------------------------------ */
  const canGenerateByQuota =
    !quota.loading &&
    !quota.is_blocked &&
    !quota.payment_blocked &&
    (quota.unlimited || (quota.remaining || 0) > 0);

  /* ------------------------------ Generate ------------------------------ */
  const handleGenerate = useCallback(
    async (imageUrl, quality) => {
      setError(null);

      // ✅ No analyzed product yet → send user to Analyze
      if (!projectId) {
        setError(
          "Please analyze a product first so we can save your generated assets."
        );
        navigate("/analyze");
        return;
      }

      // account blocked
      if (quota.is_blocked) {
        setError("Your account is blocked. Please contact support.");
        return;
      }

      // billing/quota gating → Pricing
      if (quota.payment_blocked) {
        navigate("/pricing");
        return;
      }
      if (!quota.unlimited && (quota.remaining || 0) <= 0) {
        navigate("/pricing");
        return;
      }

      try {
        const token = await getToken();

        // optimistic temp spinner
        const tempId = `temp-${Date.now()}`;
        setItems((prev) => {
          const next = [{ id: tempId, url: "", status: "pending" }, ...prev];
          setPage(1);
          pageRef.current = 1;
          recalcHasMore(next, 1);
          return next;
        });

        // queue (server returns asset id)
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/enhancer/enhance`,
          { imageUrl, projectId, quality },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const placeholder = res?.data?.asset;
        if (placeholder?.id) {
          setItems((prev) =>
            prev.map((it) =>
              it.id === tempId
                ? { ...it, id: placeholder.id, status: "queued" }
                : it
            )
          );
        }

        // Optimistic quota decrement (pending counts as used)
        if (!quota.unlimited) {
          setQuota((q) => ({
            ...q,
            remaining: Math.max(0, (q.remaining || 0) - 1),
            used: (q.used || 0) + 1,
          }));
        }

        void beginPolling();
      } catch (e) {
        const status = e?.response?.status;
        const code = e?.response?.data?.code;

        // eslint-disable-next-line no-console
        console.error("Enhancer queue failed:", e?.response?.data || e.message);

        if (status === 401) {
          setError("Session expired. Please sign in again.");
        } else if (status === 403 && code === "account_blocked") {
          setError("Your account is blocked. Please contact support.");
        } else if (
          status === 402 &&
          (code === "images_quota_exceeded" || code === "payment_required")
        ) {
          navigate("/pricing");
          setError(
            code === "payment_required"
              ? "Your subscription needs attention. Please update billing."
              : "You’ve reached your image limit for this cycle."
          );
        } else {
          setError(e?.response?.data?.error || "Failed to enhance image.");
        }

        setItems((prev) => {
          const next = prev.filter((x) => !String(x.id).startsWith("temp-"));
          recalcHasMore(next, pageRef.current);
          return next;
        });

        void fetchQuota();
      }
    },
    [getToken, projectId, beginPolling, quota, navigate, recalcHasMore, fetchQuota]
  );

  /* ------------------------- Download / Delete ------------------------- */
  const toCloudinaryDownloadUrl = useCallback((url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      if (u.hostname.includes("cloudinary") && u.pathname.includes("/upload/")) {
        u.pathname = u.pathname.replace("/upload/", "/upload/fl_attachment/");
        return u.toString();
      }
    } catch {
      // ignore
    }
    return url;
  }, []);

  const handleDownload = useCallback(
    (url) => {
      const dl = toCloudinaryDownloadUrl(url);
      const a = document.createElement("a");
      a.href = dl;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [toCloudinaryDownloadUrl]
  );

  const handleDelete = useCallback(
    async (id, url) => {
      if (String(id).startsWith("temp-")) {
        setItems((prev) => {
          const next = prev.filter((img) => img.id !== id);
          recalcHasMore(next, pageRef.current);
          return next;
        });
        return;
      }

      if (!window.confirm("Delete this image?")) return;
      try {
        const token = await getToken();

        // ✅ Soft-delete endpoint (quota must NOT decrease)
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/enhancer/delete`, {
          headers: { Authorization: `Bearer ${token}` },
          data: { projectId, id },
        });

        setItems((prev) => {
          const next = prev.filter((i) => String(i.id) !== String(id) && i.url !== url);
          if (preview?.id === id) setPreview(null);
          recalcHasMore(next, pageRef.current);
          return next;
        });

        // deletes are soft-delete; quota usage remains the same
        void fetchQuota();
      } catch (e) {
        if (e?.response?.status === 401) {
          setError("Session expired. Please sign in again.");
          return;
        }
        // eslint-disable-next-line no-alert
        alert("Delete failed");
      }
    },
    [getToken, preview, recalcHasMore, fetchQuota, projectId]
  );

  /* --------------------------- Infinite scroll --------------------------- */
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || projectLoading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, projectLoading, loadingMore, loadMore]);

  /* --------------------------- Preview close --------------------------- */
  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current) {
        setPreview(null);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  /* ------------------------------- quota UI ------------------------------ */
  // ✅ Safety: only count tier cap when subscription is active/trialing; add-ons always count.
  const hasActiveSubscription = useMemo(() => {
    const s = String(quota.sub_status || "").toLowerCase();
    return s === "active" || s === "trialing";
  }, [quota.sub_status]);

  const effectiveBaseCap =
    quota.unlimited ? (quota.baseCap || 0) : hasActiveSubscription ? (quota.baseCap || 0) : 0;

  const totalImages = quota.unlimited
    ? Infinity
    : effectiveBaseCap + (quota.imagesExtra || 0);

  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading && !quota.unlimited && Number.isFinite(totalImages) && totalImages > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalImages) * 100)))
    : 0;

  const isInitialLoading = (projectLoading || quota.loading) && !error && items.length === 0;

  /* ---------------------------------- UI ---------------------------------- */
  return (
    <div className="w-full px-4 sm:px-8 py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-8 py-10 shadow-lg">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Enhancer</h1>
            <p className="text-white/70">
              Upscale and improve clarity using Phot.AI’s Enhancer.
            </p>
          </div>

          {/* Quota pill */}
          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : quota.is_blocked
                  ? "border-red-500/40 bg-red-500/10"
                  : quota.payment_blocked
                  ? "border-amber-500/40 bg-amber-500/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                quota.is_blocked
                  ? "Account blocked"
                  : quota.payment_blocked
                  ? "Billing issue"
                  : quota.unlimited
                  ? "Unlimited images this cycle"
                  : "Images usage this cycle"
              }
            >
              {quota.is_blocked ? (
                <span className="text-red-300 font-semibold text-sm sm:text-base">
                  Account blocked
                </span>
              ) : quota.payment_blocked ? (
                <span className="text-amber-300 font-semibold text-sm sm:text-base">
                  Billing issue
                </span>
              ) : quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited images
                </span>
              ) : (
                <span className="text-white/80 text-sm sm:text-base">
                  <span className="font-semibold text-white">{remaining}</span> left of{" "}
                  <span className="font-semibold text-white">
                    {totalImages === Infinity ? "∞" : totalImages}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Usage bar */}
        {showBar && (
          <div className="mb-6 mt-2">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {totalImages === Infinity ? "∞" : totalImages}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 flex items-center gap-2 text-red-400">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {isInitialLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader />
          </div>
        ) : (
          <div
            className="
              grid gap-6
              grid-cols-1
              sm:grid-cols-2
              md:grid-cols-3
              lg:grid-cols-4
              [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]
            "
          >
            {/* Create Card (always shown; routes to Analyze/Pricing like Remove BG) */}
            <div
              onClick={() => {
                if (!projectId) {
                  navigate("/analyze");
                  return;
                }
                if (!canGenerateByQuota) {
                  navigate("/pricing");
                  return;
                }
                setModalOpen(true);
              }}
              className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg min-h-[160px] h-[210px] sm:h-[300px] text-center p-6 transition ${
                !projectId || !canGenerateByQuota
                  ? "opacity-60 cursor-not-allowed"
                  : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
              }`}
              title={
                !projectId
                  ? "Analyze a product first to start generating"
                  : !canGenerateByQuota
                  ? "Unavailable. Click to manage plan."
                  : "Choose an image and quality (2K/4K)"
              }
            >
              <Plus className="w-8 h-8 mb-2" />
              <p className="font-semibold text-base">Enhance an Image</p>
              <p className="text-sm text-gray-400 mt-1">
                {!projectId
                  ? "Analyze your first product to get started"
                  : quota.is_blocked
                  ? "Your account is blocked."
                  : quota.payment_blocked
                  ? "Please fix billing to continue."
                  : quota.unlimited
                  ? "Unlimited images"
                  : `You have ${remaining} left of ${
                      totalImages === Infinity ? "∞" : totalImages
                    } this cycle`}
              </p>

              {!!projectId && !canGenerateByQuota && !quota.is_blocked && (
                <p className="text-xs text-red-300 mt-2">
                  You’ve reached your image limit or billing needs attention.
                </p>
              )}
              {!!projectId && quota.is_blocked && (
                <p className="text-xs text-red-300 mt-2">
                  Contact support to regain access.
                </p>
              )}
            </div>

            {/* Thumbnails */}
            {visibleItems.map(({ id, url, status }) => {
              const isFailed = status === "failed";
              const PENDING = new Set(["pending", "queued", "processing"]);
              const isPending = !isFailed && (PENDING.has(status) || !url);

              const displayUrl = isPending ? "/spinner.gif" : url;

              return (
                <div
                  key={id}
                  className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-3 flex items-center justify-center transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] overflow-hidden"
                >
                  {isFailed ? (
                    <>
                      <div className="w-full h-full flex flex-col items-center justify-center text-center px-4">
                        <div className="text-red-300 font-semibold mb-2">
                          Generation failed
                        </div>
                        <div className="text-white/60 text-xs">
                          Delete this tile and try again.
                        </div>
                      </div>
                      <div className="absolute top-2 right-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(id, url);
                          }}
                          className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </>
                  ) : isPending ? (
                    <>
                      <img
                        src="/spinner.gif"
                        alt="Generating..."
                        className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                      <div className="absolute top-2 right-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(id, url);
                          }}
                          className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={displayUrl}
                        alt="Enhanced"
                        className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                        onClick={() => setPreview({ url: displayUrl, id })}
                        onError={(e) => {
                          // eslint-disable-next-line no-param-reassign
                          e.currentTarget.src = placeholderImage;
                        }}
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                      <div
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                        style={{ background: "rgba(21, 21, 43, 0.15)" }}
                      >
                        <div className="flex gap-3 pointer-events-auto">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(displayUrl);
                            }}
                            className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Download"
                          >
                            <Download size={20} />
                          </button>
                          <a
                            href={displayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Open in new tab"
                          >
                            <ExternalLink size={20} />
                          </a>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(id, displayUrl);
                            }}
                            className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Infinite loader */}
        {hasMore && !projectLoading && (
          <div
            ref={loaderRef}
            className="w-full flex justify-center py-8 pointer-events-none"
          >
            {loadingMore && (
              <img
                src="/spinner.gif"
                alt="Loading..."
                className="w-7 h-7 mx-auto"
              />
            )}
          </div>
        )}

        {modalOpen && (
          <GenerateEnhancerModal
            productImages={productImages}
            onClose={() => setModalOpen(false)}
            onGenerate={handleGenerate}
          />
        )}

        {/* Preview Modal */}
        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
            style={{ animation: "fadeIn 0.18s" }}
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl flex flex-col">
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>

              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>

              <div className="flex justify-center gap-4 pb-5">
                <button
                  type="button"
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(preview.id, preview.url)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          </div>
        )}

        {error && <div className="text-red-500 mt-6 px-6">{error}</div>}
      </div>
    </div>
  );
}
